import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  resolveAttachmentMedia,
  uploadAssetPath,
} from "@/lib/attachment-media";
import { resolveApiUrl } from "@/api/client";
import { isTauri } from "@/lib/is-tauri";

/** Access grants stay in component memory and refresh before they expire. */
export function useAttachmentUrl(source: string) {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDesktopChatAuthScope,
  );
  const [state, setState] = useState<{
    source: string;
    scope: typeof scope;
    url: string;
    error?: string;
  }>();
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);
  const managed = Boolean(uploadAssetPath(source));
  useEffect(() => {
    if (!managed) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async (force = false) => {
      try {
        const result = await resolveAttachmentMedia(source, { refresh: force });
        if (!active) return;
        setState({ source, scope, url: result.url });
        if (Number.isFinite(result.expiresAt))
          timer = setTimeout(
            () => void load(true),
            Math.max(1000, result.expiresAt - Date.now() - 30_000),
          );
      } catch (error) {
        if (active)
          setState({
            source,
            scope,
            url: "",
            error:
              error instanceof Error
                ? error.message
                : "Attachment unavailable.",
          });
      }
    };
    void load(attempt > 0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [source, scope, attempt, managed]);
  return {
    url: managed
      ? state?.source === source && state.scope === scope
        ? state.url
        : isTauri()
          ? undefined
          : resolveApiUrl(uploadAssetPath(source) ?? source)
      : resolveApiUrl(source),
    error:
      state?.source === source && state.scope === scope
        ? state.error
        : undefined,
    refresh,
  };
}
